// Copyright (C) 2024 Streaming Server Wrapper for Browser Streaming

const { BROWSER_CONFIG } = require('./cloudflare-config');
const BrowserStreamingService = require('./browser-streaming');

class StreamingServerWrapper {
    constructor() {
        this.browserStreamingService = new BrowserStreamingService();
        this.originalStreamingServer = null;
        this.isBrowserMode = BROWSER_CONFIG.enableBrowserStreaming;
    }

    // Wrap the streaming server to use browser streaming
    wrapStreamingServer(originalServer) {
        this.originalStreamingServer = originalServer;
        
        if (!this.isBrowserMode) {
            return originalServer;
        }

        // Create a proxy that intercepts streaming requests
        return new Proxy(originalServer, {
            get: (target, prop) => {
                if (prop === 'selected' && target.selected) {
                    return this.wrapSelectedStream(target.selected);
                }
                if (prop === 'baseUrl' && BROWSER_CONFIG.enableBrowserStreaming) {
                    // Return a virtual URL that indicates browser mode
                    return 'browser://localhost:8080';
                }
                return target[prop];
            }
        });
    }

    // Wrap the selected stream to use browser streaming
    wrapSelectedStream(originalSelected) {
        if (!originalSelected || !originalSelected.transportUrl) {
            return originalSelected;
        }

        // Create a new transport URL that our browser streaming service can handle
        const browserTransportUrl = this.createBrowserTransportUrl(originalSelected.transportUrl);

        return {
            ...originalSelected,
            transportUrl: browserTransportUrl,
            statistics: originalSelected.statistics || null
        };
    }

    // Create a browser-compatible transport URL
    createBrowserTransportUrl(originalUrl) {
        // If it's already a Cloudflare optimizer URL, use it directly
        const cloudflareOptimizers = [
            'torrentio.strem.fun',
            'comet.elfhosted.com',
            'mediafusion.elfhosted.com',
            'annatar.elfhosted.com',
            'jackettio.elfhosted.com',
            'orionoid.com',
            'stremthru.elfhosted.com'
        ];

        try {
            const urlObj = new URL(originalUrl);
            if (cloudflareOptimizers.some(domain => urlObj.hostname.includes(domain))) {
                return originalUrl;
            }
        } catch (error) {
            // If URL parsing fails, continue with transformation
        }

        // Convert magnet/torrent links to Cloudflare format
        if (originalUrl.includes('magnet:') || originalUrl.includes('.torrent')) {
            return `https://comet.elfhosted.com/stream?url=${encodeURIComponent(originalUrl)}`;
        }

        // For other URLs, ensure they're HTTPS and CORS-enabled
        if (originalUrl.startsWith('http://')) {
            return originalUrl.replace('http://', 'https://');
        }

        return originalUrl;
    }

    // Initialize browser streaming service
    async initializeBrowserStreaming() {
        if (!this.isBrowserMode) {
            return false;
        }

        try {
            await this.browserStreamingService.initialize();
            console.log('Browser streaming service initialized successfully');
            return true;
        } catch (error) {
            console.error('Failed to initialize browser streaming service:', error);
            return false;
        }
    }

    // Check if browser streaming is available
    isBrowserStreamingAvailable() {
        return this.isBrowserMode && this.browserStreamingService.isSupported();
    }

    // Get browser streaming service instance
    getBrowserStreamingService() {
        return this.browserStreamingService;
    }
}

// Create singleton instance
const streamingServerWrapper = new StreamingServerWrapper();

module.exports = streamingServerWrapper;